import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, useFormikContext } from 'formik';
import axios from 'axios';
import { Button } from '../../Button/button';
import './TabContent.css';
import AlertPopup from './AlertPopup';
import ConfirmationModal from './ConfirmationModal';

const ServicesTab = ({ userDetails, onUserUpdate }) => {
    const [availableServices, setAvailableServices] = useState([]);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [alert, setAlert] = useState({ message: '', type: 'info' });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemName: '' });
    const [customizedService, setCustomizedService] = useState("");
    const [isNewService, setIsNewService] = useState(false);
    useEffect(() => {
        // Fetch all available services for dropdown
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/services`)
            .then(res => setAvailableServices(res.data))
            .catch(() => setAvailableServices([]));
    }, []);

    if (!userDetails) {
        return (
            <div className="loaderContainer">
                <div className="loader"></div>
            </div>
        );
    }

    const FormObserver = ()=>{
        const {values} = useFormikContext();
        useEffect(()=>{
            const selectedValue = values.serviceId;

            if(selectedValue == "customized"){
                setIsNewService(true);
            }
            else{
                setIsNewService(false);
            }
        }, [values.serviceId])
        return null;
    }
    // Helper: get current user services as array of {id, name, price, duration, userServiceId}
    const userServices = (userDetails.userServices || []).map(us => ({
        id: us.service?.id,
        name: us.service?.name,
        price: us.price,
        duration: us.duration,
        userServiceId: us.id
    }));

    // Helper: get IDs of services already offered
    const offeredServiceIds = userServices.map(s => s.id);

    // Add new service handler
    const handleAddService = async (values, { resetForm, setSubmitting }) => {
        //if the user want to create their own service, then add that in the db first.
        console.log("Start adding in service");
        var customizedService = {id: null, name: null};
        console.log("values: ", values);
        if(values.serviceId == "customized"){
            if(availableServices.find(s=> s.name.toLowerCase() === values.customizedService.toLowerCase()) == undefined || availableServices.find(s=> s.name.toLowerCase() === values.customizedService.toLowerCase()).id == null){  //if there is no existing service in db
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/services`, { name: values.customizedService})
                .then(res=>{
                    customizedService = res.data;
                })
                .catch(err=>{
                    console.log("Error creating customized service:", err);
                    setAlert({
                        message: 'Failed to create custom service. Please try again.',
                        type: 'error'
                    });
                    setSubmitting(false);
                    return;
                })
            }
            else{
                setAlert({
                    message: "A service with this name already exists. Please choose a different name or select it from the dropdown.",
                    type: 'error'
                });
                setSubmitting(false);
            }
        }
        
        setSubmitting(true);
        // Compose new list of services/price/duration for update
        const newServices = [...userServices, {
            id: customizedService.id != null || customizedService.id != undefined? customizedService.id : values.serviceId,
            name: customizedService.name != null|| customizedService.name != undefined? customizedService.name : availableServices.find(s => s.id === Number(values.serviceId))?.name,
            price: values.price,
            duration: values.duration || 60
        }];
        console.log("newServices: ", newServices);
        // Prepare payload for backend
        const payload = {
            services: newServices.map(s => s.id),
            servicePrices: newServices.reduce((acc, s) => { acc[s.id] = s.price; return acc; }, {}),
            serviceDurations: newServices.reduce((acc, s) => { acc[s.id] = s.duration || 60; return acc; }, {}),
            description: userDetails.bio
        };
        console.log(payload);
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/${userDetails.id}`, payload)
            .then(res => {
                setAlert({
                    message: `Service added. Please note that the final price will be 12% off due to service fee}`,
                    type: 'success'
                });
                setAdding(false);
                //TODO: This part is stuck because with addion of customized service, serviceID can be "customized"
                if (onUserUpdate && values.serviceId != "customized") {  //s is indicating the service object.
                    console.log(newServices.find(s=> Number(s.id) == Number(values.serviceId)).name);
                    onUserUpdate({ ...userDetails, userServices: [...(userDetails.userServices || []), { service: { id: values.serviceId, name: newServices.find(s=> Number(s.id) == Number(values.serviceId)).name }, price: values.price, duration: values.duration || 60, id: Math.random() }] });
                }
                else if(customizedService.id != null || customizedService.id != undefined){
                    onUserUpdate({ ...userDetails, userServices: [...(userDetails.userServices || []), { service: { id: values.serviceId, name: newServices.find(s=> Number(s.id) == Number(customizedService.id)).name }, price: values.price, duration: values.duration || 60, id: Math.random() }] });
                }
            })
            .catch((err) => {
                setAlert({
                    message: 'Failed to add service. Please try again.',
                    type: 'error'
                });
                console.log(err);
            });
        setSubmitting(false);
        resetForm();
    };

    // Edit service handler
    const handleEditService = async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true);
        const updatedServices = userServices.map(s =>
            s.id === editing.id
                ? { ...s, price: values.price, duration: values.duration || 60 }
                : s
        );

        const payload = {
            services: updatedServices.map(s => s.id),
            servicePrices: updatedServices.reduce((acc, s) => { acc[s.id] = s.price; return acc; }, {}),
            serviceDurations: updatedServices.reduce((acc, s) => { acc[s.id] = s.duration || 60; return acc; }, {}),
            description: userDetails.bio
        };

        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/${userDetails.id}`, payload)
            .then(res => {
                setAlert({
                    message: 'Service updated successfully!',
                    type: 'success'
                });
                setEditing(null);
                if (onUserUpdate) {
                    onUserUpdate({
                        ...userDetails,
                        userServices: userDetails.userServices.map(us =>
                            us.service?.id === editing.id
                                ? { ...us, price: values.price, duration: values.duration || 60 }
                                : us
                        )
                    });
                }
            })
            .catch(() => {
                setAlert({
                    message: 'Failed to update service. Please try again.',
                    type: 'error'
                });
            });
        setSubmitting(false);
        resetForm();
    };

    // Remove service handler
    const handleRemoveService = async (serviceId) => {
        const newServices = userServices.filter(s => s.id !== serviceId);
        const payload = {
            services: newServices.map(s => s.id),
            servicePrices: newServices.reduce((acc, s) => { acc[s.id] = s.price; return acc; }, {}),
            serviceDurations: newServices.reduce((acc, s) => { acc[s.id] = s.duration || 60; return acc; }, {}),
            description: userDetails.bio
        };
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/${userDetails.id}`, payload)
            .then(res => {
                if (onUserUpdate) {
                    onUserUpdate({ ...userDetails, userServices: userDetails.userServices.filter(us => us.service?.id !== serviceId) });
                }
            })
            .catch(() => {
                setAlert({
                    message: 'Failed to remove service. Please try again.',
                    type: 'error'
                });
            });
    };

    const openDeleteModal = (serviceId, serviceName) => {
        setDeleteModal({
            isOpen: true,
            itemId: serviceId,
            itemName: serviceName
        });
    };

    const closeDeleteModal = () => {
        setDeleteModal({
            isOpen: false,
            itemId: null,
            itemName: ''
        });
    };

    const confirmDelete = () => {
        if (deleteModal.itemId) {
            handleRemoveService(deleteModal.itemId);
        }
    };

    return (
        <>
            <AlertPopup
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ message: '', type: 'info' })}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                title="Remove Service"
                message={`Are you sure you want to remove "${deleteModal.itemName}" from your services? This action cannot be undone.`}
                confirmText="Remove Service"
                cancelText="Cancel"
                type="danger"
            />

            <div className="tab-content">
                <div className="tab-header">
                    <h2>Services Offered</h2>
                    <p>Manage the services you offer to clients</p>
                </div>

                {/* Add new service */}
                {adding ? (
                    <div className="form-box mb-4" style={{ maxWidth: 600 }}>
                        <h4>Add New Service</h4>
                        <Formik
                            initialValues={{ serviceId: '', price: '', duration: 60, customizedService: '' }}
                            validate={values => {
                                const errors = {};
                                if (!values.serviceId && !values.customizedService) errors.serviceId = 'Select a service';
                                if (!values.price || isNaN(values.price) || Number(values.price) <= 0) errors.price = 'Enter a valid price';
                                if (!values.duration || isNaN(values.duration) || Number(values.duration) <= 0) errors.duration = 'Enter a valid duration';
                                return errors;
                            }}
                            onSubmit={handleAddService}
                        >
                            {({ isSubmitting, errors, touched }) => (
                                <Form>
                                    <FormObserver />
                                    <div className="mb-3">
                                        <label className="form-label">Service</label>
                                        <Field as="select" name="serviceId" className="form-select">
                                            <option value="">Select a service</option>
                                            {availableServices.filter(s => !offeredServiceIds.includes(s.id)).map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                            <option value="customized">Other</option>
                                        </Field>
                                        {errors.serviceId && touched.serviceId && <div className="text-danger">{errors.serviceId}</div>}
                                    </div>
                                    {isNewService? 
                                    <div className="mb-3">
                                        <label className="form-label">Custom Service Name</label>
                                        <Field name="customizedService" className="form-control" placeholder="Enter Yoga" />
                                        {errors.customizedService && touched.customizedService && <div className="text-danger">{errors.customizedService}</div>}
                                    </div>
                                    :
                                    <></> }
                                    <div className="mb-3">
                                        <label className="form-label">Price</label>
                                        <Field name="price" className="form-control" placeholder="Enter price (e.g., 75)"/>
                                        {errors.price && touched.price && <div className="text-danger">{errors.price}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Duration (minutes)</label>
                                        <Field name="duration" className="form-control" placeholder="Enter duration (e.g., 60)" />
                                        {errors.duration && touched.duration && <div className="text-danger">{errors.duration}</div>}
                                    </div>
                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button type="button" buttonStyle="btn--secondary" onClick={() => setAdding(false)}>Cancel</Button>
                                        <Button type="submit" buttonStyle="btn--primary" disabled={isSubmitting}>Add Service</Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                ) : (
                    <div className="d-flex justify-content-end mb-4" style={{ maxWidth: 600 }}>
                        <Button type="button" buttonStyle="btn--primary" onClick={() => setAdding(true)}>
                            Add New Service
                        </Button>
                    </div>
                )}

                {/* Edit service form */}
                {editing && (
                    <div className="form-box mb-4" style={{ maxWidth: 600, background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                        <h4>Edit Service: {editing.name}</h4>
                        <Formik
                            initialValues={{
                                price: editing.price || '',
                                duration: editing.duration || 60
                            }}
                            validate={values => {
                                const errors = {};
                                if (!values.price || isNaN(values.price) || Number(values.price) <= 0) errors.price = 'Enter a valid price';
                                if (!values.duration || isNaN(values.duration) || Number(values.duration) <= 0) errors.duration = 'Enter a valid duration';
                                return errors;
                            }}
                            onSubmit={handleEditService}
                        >
                            {({ isSubmitting, errors, touched }) => (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label">Price</label>
                                        <Field name="price" className="form-control" placeholder="Enter price (e.g., 75)" />
                                        {errors.price && touched.price && <div className="text-danger">{errors.price}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Duration (minutes)</label>
                                        <Field name="duration" className="form-control" placeholder="Enter duration (e.g., 60)" />
                                        {errors.duration && touched.duration && <div className="text-danger">{errors.duration}</div>}
                                    </div>
                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button type="button" buttonStyle="btn--secondary" onClick={() => setEditing(null)}>Cancel</Button>
                                        <Button type="submit" buttonStyle="btn--primary" disabled={isSubmitting}>Update Service</Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}

                {/* List of current services */}
                <div className="services-list mb-4" style={{ width: '100%', maxWidth: 600 }}>
                    {userServices.length === 0 ? (
                        <div className="no-appointments">No services offered yet.</div>
                    ) : (
                        userServices.map((service) => (
                            <div key={service.id} className="appointment-card d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <strong>{service.name}</strong>
                                    <div className="text-muted">Price: ${service.price}</div>
                                    <div className="text-muted">Duration: {service.duration || 60} min</div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        buttonStyle="btn--primary"
                                        buttonSize="btn--small"
                                        type="button"
                                        onClick={() => setEditing(service)}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        buttonStyle="btn--danger"
                                        buttonSize="btn--small"
                                        type="button"
                                        onClick={() => openDeleteModal(service.id, service.name)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

export default ServicesTab; 